
import React, { useRef } from "react";

import { fetchProfileLogo, formatSchedule } from "./printSignApi";
import { HEADER_DEFAULTS } from "./SignCanvas";
import {
    AssetPicker,
    Choice,
    Field,
    IconPicker,
    ImageAdjust,
    ItemsEditor,
    PartRow,
    ScheduleEditor,
    StringList,
    Switch,
    useAuthImage,
} from "./signUi";

/*
 * Поля одного блока вывески. Открываются, когда блок выбран на картинке или в списке.
 * Данные фирмы (название, контакты, услуги…) лежат в профиле бренда,
 * а что показывать и как (фото, колонки) — в самой вывеске.
 */

const MAX_SERVICES = 16;
const MAX_FEATURES = 8;

const SERVICE_COLUMNS = [
    { value: 0, label: "Само", hint: "по количеству" },
    { value: 1, label: "1", hint: "столбик" },
    { value: 2, label: "2", hint: "как на образце" },
    { value: 3, label: "3", hint: "для широких" },
];

/** Куски шапки: размер, сдвиг (доли шапки) и что показывать. */
const HEADER_PARTS = [
    { label: "Логотип", scale: "logoScale", x: "logoX", y: "logoY", show: "logo" },
    { label: "Название фирмы", scale: "nameScale", x: "textX", y: "textY", show: "name" },
    { label: "Подпись", scale: "taglineScale", x: "taglineX", y: "taglineY", show: "tagline" },
];
// Шаг стрелок: доля ширины и высоты шапки.
const STEP_X = 0.02;
const STEP_Y = 0.03;
const nudge = (value, delta) => Math.round(Math.min(1, Math.max(-1, Number(value || 0) + delta)) * 1000) / 1000;

const formatShift = (x = 0, y = 0) => (
    !Number(x) && !Number(y) ? "на месте" : `сдвиг ${Math.round(Number(x) * 100)}% · ${Math.round(Number(y) * 100)}%`
);

const ShowSwitch = ({ draft, name, label, locked, patchNested }) => (
    <Switch checked={draft.show[name] !== false} disabled={locked} label={label}
        onChange={(value) => patchNested("show", { [name]: value })} />
);

export const LogoBox = ({ profile, locked, busy, onPick, onRemove }) => {
    const inputRef = useRef(null);
    const logoUrl = useAuthImage(
        profile?.hasLogo ? `logo:${profile._id}:${profile.logoUpdatedAt || ""}` : "",
        () => fetchProfileLogo(profile._id)
    );

    return (
        <>
            <div className="printCatalogs__upload">
                <div className="printCatalogs__uploadBox printSigns__logoBox">
                    {profile?.hasLogo && logoUrl ? <img src={logoUrl} alt="" /> : <span>{profile?.hasLogo ? "Загрузка…" : "Логотипа нет"}</span>}
                </div>
                <div className="printCatalogs__uploadActions">
                    <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) onPick(file);
                        }} />
                    <button type="button" disabled={locked} onClick={() => inputRef.current?.click()}>
                        {busy ? "Загрузка…" : profile?.hasLogo ? "Заменить логотип" : "Загрузить логотип"}
                    </button>
                    {profile?.hasLogo ? <button type="button" className="is-quiet" disabled={locked} onClick={onRemove}>Убрать</button> : null}
                </div>
            </div>
            <p className="printCatalogs__hint">
                Лучше PNG с прозрачным фоном.{!profile ? " Профиль создастся сразу при загрузке логотипа." : ""}
                {" "}Если название уже нарисовано в логотипе — выключите «Название фирмы».
            </p>
        </>
    );
};

const BlockEditor = ({
    type,
    draft,
    patch,
    patchNested,
    profileDraft,
    patchProfile,
    profile,
    meta,
    assets,
    locked,
    logoBusy,
    onLogoPick,
    onLogoRemove,
    onAssetUploaded,
    setMessage,
}) => {
    const iconAssets = assets.filter((asset) => asset.kind === "icon");
    const shared = { draft, locked, patchNested };

    if (type === "header") {
        return (
            <div className="printSigns__blockForm">
                <LogoBox profile={profile} locked={locked} busy={logoBusy} onPick={onLogoPick} onRemove={onLogoRemove} />
                <div className="printCatalogs__grid2">
                    <Field label="Название фирмы" value={profileDraft.brandName} maxLength={80} disabled={locked}
                        placeholder="Лик Памяти" onChange={(brandName) => patchProfile({ brandName })} />
                    <Field label="Подпись под названием" value={profileDraft.tagline} maxLength={80} disabled={locked}
                        placeholder="Гранитная мастерская" onChange={(tagline) => patchProfile({ tagline })} />
                </div>
                <div className="printSigns__adjust">
                    <div className="printCatalogs__label">Логотип, название и подпись: размер, место, показать</div>
                    {HEADER_PARTS.map((item) => {
                        const header = { ...HEADER_DEFAULTS, ...(draft.header || {}) };
                        return (
                            <PartRow
                                key={item.show}
                                label={item.label}
                                scale={header[item.scale]}
                                min={0.4}
                                max={2.5}
                                visible={draft.show[item.show] !== false}
                                unavailable={item.show === "logo" && !profile?.hasLogo ? "нет логотипа" : ""}
                                disabled={locked}
                                onScale={(value) => patchNested("header", { [item.scale]: value })}
                                onNudge={(dx, dy) => patchNested("header", {
                                    [item.x]: nudge(header[item.x], dx * STEP_X),
                                    [item.y]: nudge(header[item.y], dy * STEP_Y),
                                })}
                                onToggle={(visible) => patchNested("show", { [item.show]: visible })}
                            />
                        );
                    })}
                    <p className="printCatalogs__hint">
                        100% — как задумано дизайном. Высота шапки от этого не меняется — её тянут за границу на картинке.
                        Стрелки двигают на шаг; на картинке всё тянется мышкой, колесо — больше / меньше. Название ведёт за собой подпись.
                    </p>
                    <div className="printSigns__headerReset">
                        <span>
                            Логотип {formatShift(draft.header?.logoX, draft.header?.logoY)}, название {formatShift(draft.header?.textX, draft.header?.textY)},
                            подпись {formatShift(draft.header?.taglineX, draft.header?.taglineY)}
                        </span>
                        <button type="button" className="printSigns__add" disabled={locked}
                            onClick={() => patchNested("header", { logoX: 0, logoY: 0, textX: 0, textY: 0, taglineX: 0, taglineY: 0 })}>
                            Всё на место
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (type === "features") {
        return (
            <div className="printSigns__blockForm">
                <Field label="Заголовок блока" value={profileDraft.featuresTitle} maxLength={90} disabled={locked}
                    placeholder="Современное 3D-оформление заказов" onChange={(featuresTitle) => patchProfile({ featuresTitle })} />
                <div className="printCatalogs__label">Карточки: иконка + цифра + текст</div>
                <ItemsEditor items={profileDraft.features} meta={meta} iconAssets={iconAssets} withValue max={MAX_FEATURES} disabled={locked}
                    addLabel="Карточку" onChange={(features) => patchProfile({ features })} />

                <div className="printCatalogs__label">Фото блока (например, рендер 100–200 памятников)</div>
                <AssetPicker kind="photo" assets={assets} value={draft.featuresImage.assetId} disabled={locked}
                    emptyLabel="Без фото" setMessage={setMessage} onUploaded={onAssetUploaded}
                    onChange={(assetId) => patchNested("featuresImage", { assetId })} />
                {draft.featuresImage.assetId ? (
                    <ImageAdjust value={draft.featuresImage} disabled={locked} onChange={(value) => patchNested("featuresImage", value)}>
                        <Switch checked={draft.featuresImage.fade !== false} disabled={locked} label="Плавный переход под заголовком"
                            onChange={(fade) => patchNested("featuresImage", { fade })} />
                        <p className="printCatalogs__hint">На картинке справа: тяните фото мышкой — сдвиг, колесо — увеличение.</p>
                    </ImageAdjust>
                ) : null}
            </div>
        );
    }

    if (type === "services") {
        return (
            <div className="printSigns__blockForm">
                <Field label="Заголовок блока" value={profileDraft.servicesTitle} maxLength={60} disabled={locked}
                    placeholder="Наши услуги" onChange={(servicesTitle) => patchProfile({ servicesTitle })} />
                <div className="printCatalogs__label">Услуги: иконка + название</div>
                <ItemsEditor items={profileDraft.services} meta={meta} iconAssets={iconAssets} max={MAX_SERVICES} disabled={locked}
                    addLabel="Услугу" onChange={(services) => patchProfile({ services })} />
                <div className="printCatalogs__label">Колонок</div>
                <Choice options={SERVICE_COLUMNS} value={draft.servicesColumns} disabled={locked}
                    onChange={(servicesColumns) => patch({ servicesColumns })} />
            </div>
        );
    }

    if (type === "contacts") {
        return (
            <div className="printSigns__blockForm">
                <div className="printCatalogs__switches">
                    <ShowSwitch {...shared} name="phones" label="Телефоны" />
                    <ShowSwitch {...shared} name="emails" label="Почты" />
                    <ShowSwitch {...shared} name="addresses" label="Адреса" />
                    <ShowSwitch {...shared} name="hours" label="Режим работы" />
                    <ShowSwitch {...shared} name="qr" label="QR-код" />
                    <ShowSwitch {...shared} name="button" label="Кнопка" />
                </div>

                <StringList label="Телефоны" values={profileDraft.phones} max={8} maxLength={60} disabled={locked}
                    placeholder="+7 (900) 111-11-11" addLabel="Телефон" onChange={(phones) => patchProfile({ phones })} />
                <StringList label="Почты" values={profileDraft.emails} max={6} disabled={locked}
                    placeholder="info@likpamyati.ru" addLabel="Почту" onChange={(emails) => patchProfile({ emails })} />
                <StringList label="Адреса" values={profileDraft.addresses} max={4} maxLength={200} disabled={locked}
                    placeholder="г. Кировск, ул. Центральная, д. 10" addLabel="Адрес" onChange={(addresses) => patchProfile({ addresses })} />
                <Field label="Сайт" value={profileDraft.site} maxLength={120} disabled={locked}
                    placeholder="likpamyati.ru" onChange={(site) => patchProfile({ site })} />

                <div className="printCatalogs__label">Режим работы — на вывеске: {formatSchedule(profileDraft.schedule).join(" · ")}</div>
                <Field label="Заголовок" value={profileDraft.hoursTitle} maxLength={40} disabled={locked}
                    placeholder="Режим работы:" onChange={(hoursTitle) => patchProfile({ hoursTitle })} />
                <ScheduleEditor schedule={profileDraft.schedule} disabled={locked} onChange={(schedule) => patchProfile({ schedule })} />

                <div className="printCatalogs__grid2">
                    <Field label="Ссылка в QR (пусто — сайт)" value={profileDraft.qrUrl} maxLength={300} disabled={locked}
                        placeholder="https://alotb.ru" onChange={(qrUrl) => patchProfile({ qrUrl })} />
                    <Field label="Подпись у QR" value={profileDraft.qrCaption} maxLength={120} disabled={locked}
                        placeholder="Сканируйте для перехода в онлайн-офис" onChange={(qrCaption) => patchProfile({ qrCaption })} />
                </div>
                <div className="printCatalogs__label">Кнопка внизу</div>
                <div className="printSigns__itemRow">
                    <IconPicker value={{ icon: profileDraft.buttonIcon, assetId: null }} meta={meta} disabled={locked}
                        onChange={(value) => patchProfile({ buttonIcon: value.icon })} />
                    <input type="text" value={profileDraft.buttonText} maxLength={40} disabled={locked} placeholder="Оформить заказ"
                        onChange={(event) => patchProfile({ buttonText: event.target.value })} />
                </div>
            </div>
        );
    }

    return null;
};

export default BlockEditor;
